import React, { JSX, useEffect, useState } from "react";
import { Table, TableColumnsType, Tag } from "antd";
import { RightOutlined } from "@ant-design/icons";
import { useQuery } from "react-query";
import Text from "../../components/Text/Text";
import SortFieldCustom from "../../components/SortFieldTable/SortFieldCustom";
import useSorting from "../../hook/useSorting";
import color from "../../resource/color";
import { ShopTierCompanyConfig } from "./config";
import {
  AlignTopTableContainer,
  CountRow,
} from "./styled";
import { useNavigate } from "react-router-dom";
import { useLocalStorage } from "../../hook/useLocalStorage";
import { CustomerTierSummaryItem, getCustomerTierSummary } from "../../datasource/CustomerTierProgramDatasource";

const TAKE = 10;

interface Props {
  config: ShopTierCompanyConfig;
  search: string;
  zone?: string | null;
  tierFilter: string | null;
  status: string;
}

function ShopListTab({ config, search, zone, tierFilter, status }: Props): JSX.Element {
  const { sortField, sortDirection, handleSortChange } = useSorting();
  const [company] = useLocalStorage("profile", null);
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [search, zone, tierFilter, status]);

  const { data: summary, isLoading } = useQuery(
    ["customer-tier-summary", company?.company, tierFilter, zone, search, status, sortField, sortDirection, page],
    () =>
      getCustomerTierSummary({
        company: company?.company ?? "",
        tierLevelId: tierFilter ?? undefined,
        customerZone: zone ?? undefined,
        search: search || undefined,
        status: status !== "ALL" ? status : "ALL",
        sortBy: sortField || "updatedAt",
        sortOrder: sortDirection || "DESC",
        page,
        take: TAKE,
      }),
    { enabled: !!company?.company, refetchOnWindowFocus: false, keepPreviousData: true },
  );

  // ยังไม่ยืนยัน response schema จริงกับ backend — สมมติตาม responseData.data/.count เหมือน getCustomerTierOverview
  const shops: CustomerTierSummaryItem[] = summary?.responseData?.data || [];
  const total: number = summary?.responseData?.count || 0;

  const tierColorOf = (tierKey: string) => config.tiers.find((t) => t.key === tierKey)?.color || color.Grey;

  const formatThaiDate = (value?: string | null): string => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear() + 543;
    return `${day}/${month}/${year}`;
  };

  const formatAccruedTotal = (value?: number | null): string => {
    if (value === null || value === undefined) return "-";
    if (value <= 1_000_000) return value.toLocaleString("th-TH");
    return `${(value / 1_000_000).toFixed(2)}M฿`;
  };

  const columns: TableColumnsType<CustomerTierSummaryItem> = [
    {
      title: (
        <SortFieldCustom
          title="รหัสร้านค้า"
          sortDirection={sortField === "customerNo" ? sortDirection : undefined}
          onSortChange={() => handleSortChange("customerNo")}
        />
      ),
      dataIndex: "customerNo",
      key: "customerNo",
      render: (tier: string) => <Text fontFamily="Sarabun" fontSize={14} fontWeight={500} color="Text2">{tier}</Text>,
    },
    {
      title: (
        <SortFieldCustom
          title="ร้านค้า"
          sortDirection={sortField === "customerName" ? sortDirection : undefined}
          onSortChange={() => handleSortChange("customerName")}
        />
      ),
      dataIndex: "customerName",
      key: "customerName",
      render: (tier: string) => <Text fontFamily="Sarabun" fontSize={14} fontWeight={500} color="Text2">{tier}</Text>,
    },
    { title: "จังหวัด", dataIndex: "customerProvince", key: "customerProvince",render: (tier: string) => <Text fontFamily="Sarabun" fontSize={14} fontWeight={500} color="Text2">{tier}</Text>, },
    { title: "เขต", dataIndex: "customerZone", key: "customerZone", render: (tier: string) => <Text fontFamily="Sarabun" fontSize={14} fontWeight={500} color="Text2">{tier}</Text>, },
    {
      title: (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
          <SortFieldCustom
            title="ระดับปัจจุบัน"
            sortDirection={sortField === "currentTierRank" ? sortDirection : undefined}
            onSortChange={() => handleSortChange("currentTierRank")}
          />
          <Text fontSize={12} fontWeight={400} color="Text3">
            ปี 2569
          </Text>
        </div>
      ),
      dataIndex: "currentTierName",
      key: "currentTierName",
      render: (tier: string | null, record: CustomerTierSummaryItem) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "flex-start" }}>
          {tier ? (
            <Tag style={{ borderRadius: "16px" }} color={tierColorOf(tier)}>
              <Text fontFamily="Sarabun" fontSize={14} fontWeight={700} color="white">
                {tier}
              </Text>
            </Tag>
          ) : (
            <Text fontFamily="Sarabun" fontSize={14} fontWeight={500} color="Text2">-</Text>
          )}
          {record.isPendingReview && (
            <Text fontFamily="Sarabun" fontSize={12} fontWeight={500} color="warning">
              รอพิจารณาปรับระดับ
            </Text>
          )}
        </div>
      ),
    },
    ...(company?.company === "ICPL"
      ? [
          {
            title: (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                <Text fontSize={16} fontWeight={600} color="Text2">
                  ระดับคาดการณ์
                </Text>
                <Text fontSize={12} fontWeight={400} color="Text3">
                  ประมาณการจากยอดปีนี้
                </Text>
              </div>
            ),
            dataIndex: "earnedTierName",
            key: "earnedTierName",
            render: (tier: string | null) => (
              <div style={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "flex-start" }}>
                {tier ? (
                  <Tag style={{ borderRadius: "16px" }} color={tierColorOf(tier)}>
                    <Text fontFamily="Sarabun" fontSize={14} fontWeight={700} color="white">
                      {tier}
                    </Text>
                  </Tag>
                ) : (
                  <Text fontFamily="Sarabun" fontSize={14} fontWeight={500} color="Text2">-</Text>
                )}
              </div>
            ),
          } as TableColumnsType<CustomerTierSummaryItem>[number],
        ]
      : []),
    {
      title: (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
          <SortFieldCustom
            title="ยอดสะสม"
            sortDirection={sortField === "accruedTotal" ? sortDirection : undefined}
            onSortChange={() => handleSortChange("accruedTotal")}
          />
          <Text fontSize={12} fontWeight={400} color="Text3">
            {config.volumeUnit} ปี 2569
          </Text>
        </div>
      ),
      dataIndex: "accruedTotal",
      key: "accruedTotal",
      align: "right",
      render: (tier: number | null) => <Text fontFamily="Sarabun" fontSize={14} fontWeight={500} color="Text2">{formatAccruedTotal(tier)}</Text>,
    },
    {
      title: (
        <SortFieldCustom
          title="เปลี่ยนระดับล่าสุด"
          sortDirection={sortField === "tierChangedAt" ? sortDirection : undefined}
          onSortChange={() => handleSortChange("tierChangedAt")}
        />
      ),
      dataIndex: "tierChangedAt",
      key: "tierChangedAt",
      render: (tier: string | null) => <Text fontFamily="Sarabun" fontSize={14} fontWeight={500} color="Text2">{formatThaiDate(tier)}</Text>,
    },
    {
      title: "ประวัติ",
      dataIndex: "historyCount",
      key: "historyCount",
      render: (tier: number | null) => (
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <Text fontFamily="Sarabun" fontSize={14} fontWeight={500} color="Text2">
            {tier} รายการ
          </Text>
          <RightOutlined style={{ color: color.Text3, fontSize: 12 }} />
        </div>
      ),
    },
  ];

  return (
    <div>
      <CountRow>{total} รายการ</CountRow>
      <AlignTopTableContainer>
        <Table
          rowKey="customerTierId"
          dataSource={shops}
          columns={columns}
          loading={isLoading}
          scroll={{ x: "max-content" }}
          pagination={{ current: page, pageSize: TAKE, total, onChange: setPage }}
          onRow={(record) => ({
            style: { cursor: "pointer" },
            onClick: () => navigate(`/ShopTier/detail/${record.customerTierId}`),
          })}
        />
      </AlignTopTableContainer>
    </div>
  );
}

export default ShopListTab;
